import i18n from "../../../config/i18n";
import { SupportedLanguage } from "../../../interfaces";
import { normalizeLanguage } from "./langUtils";

function getLocaleDateFormatOptions(
  locale: SupportedLanguage,
  shorten?: boolean
): Intl.DateTimeFormatOptions {
  switch (locale) {
    case "en": {
      return shorten === true
        ? { year: "numeric", month: "2-digit", day: "2-digit" }
        : { year: "numeric", month: "long", day: "numeric" };
    }
    default: {
      return shorten === true
        ? { year: "numeric", month: "2-digit", day: "2-digit" }
        : { year: "numeric", month: "long", day: "numeric" };
    }
  }
}

export function renderDate(date: Date | string, shorten?: boolean) {
  if (typeof date === "string") {
    date = new Date(date);
  }
  const lang = normalizeLanguage(i18n.resolvedLanguage);
  const options = getLocaleDateFormatOptions(lang, shorten);
  return new Intl.DateTimeFormat(lang, options).format(date);
}

/**
 * An article's publication date in full ("June 9, 2026"). The API stores it as
 * a calendar day at UTC midnight, so it reads in UTC: local time shows the day
 * before west of UTC.
 */
export function renderArticleDate(date: Date | string): string {
  const lang = normalizeLanguage(i18n.resolvedLanguage);
  return new Intl.DateTimeFormat(lang, {
    ...getLocaleDateFormatOptions(lang),
    timeZone: "UTC",
  }).format(new Date(date));
}

export function renderDateRelative(date: Date | string) {
  if (typeof date === "string") {
    date = new Date(date);
  }
  const lang = normalizeLanguage(i18n.resolvedLanguage);
  const now = new Date();
  const diff = date.getTime() - now.getTime();
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });

  const seconds = Math.round(diff / 1000);
  const minutes = Math.round(diff / (1000 * 60));
  const hours = Math.round(diff / (1000 * 60 * 60));
  const days = Math.round(diff / (1000 * 60 * 60 * 24));
  const months = Math.round(diff / (1000 * 60 * 60 * 24 * 30));
  const years = Math.round(diff / (1000 * 60 * 60 * 24 * 365));

  if (Math.abs(seconds) < 60) {
    return rtf.format(seconds, "second");
  } else if (Math.abs(minutes) < 60) {
    return rtf.format(minutes, "minute");
  } else if (Math.abs(hours) < 24) {
    return rtf.format(hours, "hour");
  } else if (Math.abs(days) < 30) {
    return rtf.format(days, "day");
  } else if (Math.abs(months) < 12) {
    return rtf.format(months, "month");
  } else {
    return rtf.format(years, "year");
  }
}

/**
 * A video, infographic or collection date in a list's meta line ("Jun 9,
 * 2026"), in the reader's locale and zone. Empty when there is no date: the
 * meta line leaves it out. An article's date goes through `formatArticleDate`.
 */
export function formatShortDate(
  value: Date | string | null,
  locale: string
): string {
  if (value === null) return "";
  return new Date(value).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** `formatShortDate` for an article's publication date, read in UTC like `renderArticleDate`. */
export function formatArticleDate(
  value: Date | string | null,
  locale: string
): string {
  if (value === null) return "";
  return new Date(value).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * When something happened, by calendar day, for a list row: "Today",
 * "Yesterday" and "3 days ago" within the week, then the day ("Sep 19"), with
 * its year once it is not this year's. In the reader's locale.
 */
export function renderDayRelative(value: Date | string, now: Date): string {
  const date = new Date(value);
  const lang = normalizeLanguage(i18n.resolvedLanguage);
  const startOfDay = (day: Date) =>
    new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
  const days = Math.round(
    (startOfDay(now) - startOfDay(date)) / (1000 * 60 * 60 * 24)
  );
  if (days >= 0 && days < 7) {
    const relative = new Intl.RelativeTimeFormat(lang, {
      numeric: "auto",
    }).format(-days, "day");
    return relative.charAt(0).toLocaleUpperCase(lang) + relative.slice(1);
  }
  return date.toLocaleDateString(
    lang,
    date.getFullYear() === now.getFullYear()
      ? { month: "short", day: "numeric" }
      : { month: "short", day: "numeric", year: "numeric" }
  );
}
